import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Gift, Ruler, ExternalLink, Heart, Palette } from 'lucide-react'
import { formatPrice, urlHostname, type WishlistItem } from '@/lib/wishlist'
import { useImageBackground } from '@/hooks/useImageBackground'
import { parseItemColors } from '@/lib/itemColors'
import { getCategoryColor, getCategoryInlineStyle, translateCategory } from '@/lib/categoryColors'
import { useLanguage } from '@/context/LanguageContext'
import ItemAppreciationButton from '@/components/wishlist/ItemAppreciationButton'

interface ItemCardProps {
  item: WishlistItem
  href?: string
  onClick?: () => void
  isOwner?: boolean
  isGuide?: boolean
  appreciation?: { count: number; liked: boolean }
  onAppreciationChange?: (appreciation: { count: number; liked: boolean }) => void
}

export default function ItemCard({ item, href, onClick, isOwner = false, isGuide = false, appreciation, onAppreciationChange }: ItemCardProps) {
  const { language } = useLanguage()
  const price = formatPrice(item.price)
  const originalPrice = formatPrice(item.original_price)
  const hasDiscount = originalPrice && price && (item.original_price ?? 0) > (item.price ?? 0)
  const autoBg = useImageBackground(item.image_url)
  const imageBg = item.bg_color || autoBg
  const badgeClass = getCategoryColor(item.category)
  const itemColors = parseItemColors(item.color, `${item.title} ${item.description ?? ''}`)

  const card = (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
      className="group relative overflow-hidden rounded-lg glass hover-glow transition-all duration-300 cursor-pointer h-full flex flex-col"
    >
      {(href || onClick) && (href ? (
        <Link
          to={href}
          aria-label={`${language === 'it' ? 'Vedi dettagli di' : 'View details of'} ${item.title}`}
          className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        />
      ) : (
        <button
          type="button"
          onClick={onClick}
          aria-label={`${language === 'it' ? 'Vedi dettagli di' : 'View details of'} ${item.title}`}
          className="absolute inset-0 z-10 h-full w-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        />
      ))}
      {isGuide && appreciation && (
        <ItemAppreciationButton
          key={item.id}
          itemId={item.id}
          initialCount={appreciation.count}
          initialLiked={appreciation.liked}
          onChange={onAppreciationChange}
        />
      )}
        <div className="relative aspect-square overflow-hidden" style={{ backgroundColor: imageBg }}>
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title}
            className={`w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 ${item.is_reserved && !isGuide ? 'opacity-50' : ''}`}
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0' }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center">
            <Gift className="w-14 h-14 text-indigo-300" />
          </div>
        )}
        {item.is_reserved && !isGuide && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.08 }}
            transition={{ type: 'spring', stiffness: 400, damping: 15 }}
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
          >
            <div
              className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs md:text-sm font-extrabold text-white shadow-lg"
              style={{ backgroundColor: '#DE256D' }}
            >
              <Heart className="w-3.5 h-3.5 fill-current" />
              Prenotato
            </div>
          </motion.div>
        )}
        <span className={`absolute top-3 left-3 inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold text-[10px] md:text-xs shadow-sm z-10 ${badgeClass}`} style={getCategoryInlineStyle(item.category)}>
          {translateCategory(item.category, language)}
        </span>

      </div>
      <div className="p-3 md:p-4 flex-1 flex flex-col">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground line-clamp-1 mb-0.5 text-sm md:text-base">
              {item.title}
            </h3>
            {(item as WishlistItem & { is_recurring?: boolean }).is_recurring && (
              <p className="text-xs md:text-sm font-semibold line-clamp-1 mb-1" style={{ color: '#23C55E' }}>
                <span className="md:hidden">Acquisto ricorrente</span>
                <span className="hidden md:inline">Consentito acquisto ricorrente</span>
              </p>
            )}
          </div>
          {itemColors.length > 0 && (
            <div className="flex items-center shrink-0" style={{ gap: '-4px' }}>
              {itemColors.map((c, i) => (
                <span
                  key={i}
                  className="w-5 h-5 md:w-6 md:h-6 rounded-full shadow-sm"
                  style={{
                    backgroundColor: c,
                    border: '3px solid white',
                    marginLeft: i === 0 ? 0 : '-8px',
                  }}
                  title={item.color ?? ''}
                />
              ))}
            </div>
          )}
        </div>
        {isGuide && item.description && (
          <p className="mt-1 text-[11px] md:text-xs text-muted-foreground line-clamp-3">
            {item.description}
          </p>
        )}
        <div className="flex flex-col gap-1 mt-1 md:flex-row md:items-center md:justify-between md:gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            {(item as WishlistItem & { is_recurring?: boolean }).is_recurring && (
              <span className="text-[10px] md:text-xs font-semibold text-neutral-500 inline-flex items-center gap-0.5 shrink-0 mb-3">
                Acquistato · {(item as WishlistItem & { purchase_count?: number }).purchase_count ?? 0} volte
              </span>
            )}
            {item.size && (
              <p className="text-[10px] md:text-xs text-muted-foreground inline-flex items-center gap-1 shrink-0">
                <Ruler className="w-3 h-3" />
                {item.size}
              </p>
            )}
            {item.color_text && (
              <p className="text-[10px] md:text-xs text-muted-foreground inline-flex items-center gap-1 shrink-0 truncate">
                <Palette className="w-3 h-3" />
                {item.color_text}
              </p>
            )}
          </div>
                    {price && (
                      <span className="flex items-baseline gap-1.5 justify-center md:justify-start md:shrink-0">
                        {hasDiscount && (
                          <span className="text-[10px] md:text-xs text-neutral-400 line-through">
                            {originalPrice}
                          </span>
                        )}
                        <span className="text-base md:text-sm font-bold md:font-semibold gradient-text-price">{price}</span>
                      </span>
                    )}
        </div>
        <div className="mb-2"></div>
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="relative z-20 mt-auto flex items-center justify-center gap-1.5 w-full rounded-full border border-neutral-200 bg-white px-4 py-2 text-[11px] md:text-xs font-semibold text-neutral-700 transition hover:border-neutral-400 hover:bg-neutral-50"
          >
            <ExternalLink className="w-3 h-3" />
            {urlHostname(item.url)}
          </a>
        )}
      </div>
    </motion.div>
  )

  return card
}
